import React from "react";
import { Modal, Form, Input, Row, Col, InputNumber, DatePicker, Select, Typography } from "antd";
import styled from "styled-components";
import {
  WarehouseLoggedHoursType,
  WarehouseLoggedHours,
} from "services/BillingEngineService/interfaces/entities/WarehouseLoggedHours";
import { hourOptions, minuteOptions, projectOptions } from "../constants";
import { Moment } from "moment";
import useQueryCreateHours from "../Api/useQueryCreateHours";
import OrganizationsSearchbar from "../Searchbars/OrganizationsSearchbar";
import { Organization } from "@secondcloset/types/src/_common";
import { WRO } from "services/BillingEngineService/interfaces/entities/WRO";
import WROSearchbar from "../Searchbars/WROSearchbar";
import colors from "css/colors";
import FacilitiesSelect from "components/FacilitiesSelect";
import moment from "moment";

const { Option } = Select;

export interface WarehouseLogHoursForm extends Omit<WarehouseLoggedHours, "date"> {
  projectType: WarehouseLoggedHoursType;
  date: Moment;
  facility?: any;
}

interface LogHoursModal {
  visible?: boolean;
  onOkay: () => void;
  onCancel: () => void;
}

const ID_LABEL_MAP: Record<WarehouseLoggedHoursType, string> = {
  wros: "WRO",
  // eslint-disable-next-line
  ["special-projects"]: "Organization",
};

const PROJECT_LABEL_MAP: Record<WarehouseLoggedHoursType, string> = {
  wros: "Receiving",
  // eslint-disable-next-line
  ["special-projects"]: "Special Projects",
};

function LogHoursModal({ visible, onOkay, onCancel }: LogHoursModal) {
  const [form] = Form.useForm();
  const [save] = useQueryCreateHours();
  const [submitError, setSubmitError] = React.useState("");

  async function onSubmit() {
    form
      .validateFields()
      .then((formValues: any) => {
        setSubmitError("");

        save(formValues, {
          onSuccess: (...args: any) => {
            form.resetFields();
            setSelectedProjectType("wros");
            onOkay();
          },
          onError: (err: any) => {
            setSubmitError(err.message);
          },
        });
      })
      .catch((error) => {});
  }

  const [selectedProjectType, setSelectedProjectType] = React.useState<WarehouseLoggedHoursType>("wros");

  function onProjectTypeSelect(value: WarehouseLoggedHoursType) {
    form.setFieldsValue({ projectType: value });
    setSelectedProjectType(value);
  }

  const idLabel = ID_LABEL_MAP[selectedProjectType];

  function onOrganizationSelect(organization?: Organization) {
    form.setFieldsValue({ id: organization?.id });
  }

  function onWROSelect(wro?: WRO) {
    form.setFieldsValue({ id: wro?.wroId });
  }

  function onFacilitySelect(facility: any) {
    form.setFieldsValue({ facility });
  }

  return (
    <Modal
      title="Log Hours"
      visible={visible}
      onOk={onSubmit}
      onCancel={onCancel}
      okText="Save"
      style={{ minWidth: 740 }}
    >
      <WarehouseLogHoursForm form={form} layout="vertical" size="large">
        <Row gutter={60}>
          <Col span={12}>
            <Form.Item
              label="Project Type"
              name="projectType"
              initialValue="wros"
              rules={[
                {
                  required: true,
                  message: "Please select a project type",
                },
              ]}
            >
              <Select onChange={onProjectTypeSelect}>
                {projectOptions.map((option) => (
                  <Option key={option} value={option}>
                    {PROJECT_LABEL_MAP[option]}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={idLabel}
              name="id"
              rules={[
                {
                  required: true,
                  message: `Please enter the ${idLabel}`,
                },
              ]}
            >
              {selectedProjectType === "special-projects" ? (
                <OrganizationsSearchbar onSelect={onOrganizationSelect} />
              ) : (
                <WROSearchbar onSelect={onWROSelect} />
              )}
            </Form.Item>
          </Col>
        </Row>

        {selectedProjectType === "special-projects" && (
          <Row gutter={60}>
            <Col span={12}>
              <Form.Item
                label="Facility"
                name="facility"
                rules={[
                  {
                    required: true,
                    message: "Please select a facility",
                  },
                ]}
              >
                <FacilitiesSelect onSelect={onFacilitySelect} />
              </Form.Item>
            </Col>
          </Row>
        )}

        <Row gutter={60}>
          <Col span={12}>
            <Form.Item
              label="Number of Workers"
              name="workersCount"
              rules={[
                {
                  required: true,
                  message: "Please select the number of workers",
                },
              ]}
            >
              <InputNumber style={{ width: "100%" }} placeholder="Enter Number" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Date"
              name="date"
              rules={[
                {
                  required: true,
                  message: "Please select the date",
                },
              ]}
            >
              <DatePicker
                style={{ width: "100%" }}
                disabledDate={(current) => current && current >= moment().endOf("day")}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={60}>
          <Col span={12}>
            <div className="hours-worked-container">
              <Form.Item
                label="Hours"
                name="hoursWorked"
                rules={[
                  {
                    required: true,
                    message: "Please select the number of hours worked",
                  },
                ]}
              >
                <Select>
                  {hourOptions.map((option) => (
                    <Option key={option} value={option}>
                      {option}h
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                label="Minutes"
                name="minutesWorked"
                rules={[
                  {
                    required: true,
                    message: "Please select the number of minutes worked",
                  },
                ]}
              >
                <Select>
                  {minuteOptions.map((option) => (
                    <Option key={option} value={option}>
                      {option} mins
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          </Col>

          <Col span={12}>
            <Form.Item label="Notes" name="notes">
              <Input.TextArea />
            </Form.Item>
          </Col>
        </Row>
        {submitError && (
          <Row>
            <Col span={24}>
              <Typography.Paragraph style={{ color: colors.RED }}>{submitError}</Typography.Paragraph>
            </Col>
          </Row>
        )}
      </WarehouseLogHoursForm>
    </Modal>
  );
}

const WarehouseLogHoursForm = styled(Form)`
  .hours-worked-container {
    display: flex;
    align-items: flex-end;

    .ant-form-item {
      flex: 1;
    }

    .ant-form-item-control-input-content {
      display: flex;
      align-items: center;

      label {
        margin: 0 20px;
      }
    }
  }
`;

export default LogHoursModal;
